/* eslint-disable no-console */

import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import ViewClassroomScreen from "../../../components/screens/Classrooms/ViewClassroomScreen";
import {
  courses as courseQuerys,
  classrooms as classroomQuerys,
  users as userQuerys,
  assignments as assignmentQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useClassroomState from "@nualang/nualang-ui-components/dist/hooks/useClassroomState";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import config, { featureFlags } from "../../../config";
import ClassroomImage from "../../../img/classroom.svg";
import MembersImage from "../../../img/members.svg";
import NoCoursesImage from "../../../img/noCourses.svg";
import QuickCreateCourseDialog from "@nualang/nualang-ui-components/dist/Dialogs/QuickCreateCourseDialog/QuickCreateCourseDialog";
import FindCoursesDialog from "@nualang/nualang-ui-components/dist/Dialogs/FindCoursesDialog/FindCoursesDialog";
import { UploadClassroomImage, UploadCourseImage } from "../../../utils/imageUpload";
import { trackRecommendedEvent, searchStringToObj } from "../../../utils/index";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getCourseSectionTopicRoleplays } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicRoleplays";
import { getCourseSectionTopicPhraseLists } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import { getMemberCourseCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  copyCourse,
  getCourses,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import { UploadUserImage } from "../../../utils/imageUpload";
import {
  getClassroom,
  updateClassroom as updateClassroomAPI,
  deleteClassroom as deleteClassroomAPI,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import {
  courses as courseHelpers,
  classrooms as classroomHelpers,
  users as usersHelpers,
  progress as progressHelpers,
  completions as completionHelpers,
  notifications as notificationHelpers,
} from "../../../utils/helpers";
import {
  createClassroomMember,
  deleteClassroomMember,
  updateClassroomMember,
  getClassroomMember,
  getClassroomMembers,
} from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import useVideoChat from "../../../hooks/useVideoChat";
import useClassroomLiveGame from "../../../hooks/useClassroomLiveGame";
import { getMeeting } from "../../../utils/api/vchat/api/Meetings";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { isClassroomDiscussAIFeedbackEnabled } from "@nualang/nualang-ui-components/dist/utils/aiControls";
import {
  getClassroomAssignments,
  getAssignmentMembers,
  deleteAssignment as deleteAssignmentAPI,
} from "@nualang/nualang-api-and-queries/APIs/Assignments";
import { makeChatGptApiRequest } from "@nualang/nualang-api-and-queries/APIs/Services";
import { createCourse as createCourseAPI } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { createCourseSection } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { useApp, useCourse, useSubscription, useClassroom } from "../../../hooks";
import logger from "../../../logger";
import { v4 as uuidv4 } from "uuid";

const { languages, forLanguages } = config;

const CLASSROOM_NOT_FOUND = "ClassroomNotFound";

function ViewClassroom(props) {
  const {
    authData,
    navigate,
    authenticated,
    handleSignIn,
    location,
    verificationStatus,
    isCommunityAccessRestricted,
  } = props;
  const params = useParams();
  const { classroomId } = params;
  const searchParams = searchStringToObj(location.search);
  const { freeTrial, newCollaborator, assignmentId } = searchParams;
  const queryClient = useQueryClient();
  const {
    user,
    setUser,
    loading,
    startLoading,
    stopLoading,
    handleOpenModerateDialog,
    openSnackbar,
    markCommunityAccessRestricted,
  } = useApp();
  const { subscription, fetchCreatorSubscription } = useSubscription();

  const { username, attributes = {} } = user;
  const { preferred_username, picture, email } = attributes;
  const {
    setNewClassroom,
    setIsJoinClassroomDialogOpen,
    memberLimit,
  } = useClassroom();
  const {
    selectedCourses,
    setNewCourse,
    setIsJoinCourseDialogOpen,
    handleClickCourse,
    setSelectedCourses,
  } = useCourse();

  const { sendCollaboratorEmail } = notificationHelpers;

  const [initialSelectedCourses, setInitialSelectedCourses] = useState([]);
  const { t, i18n } = useTranslation();
  const [confirm] = useConfirm(t);
  const [classroomMembersFormatted, setClassroomMembersFormatted] = useState(
    [],
  );

  const classroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      if (!classroom?.Item) {
        const notFoundError = new Error("Classroom not found");
        notFoundError.name = CLASSROOM_NOT_FOUND;
        throw notFoundError;
      }
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    {
      enabled: !!classroomId,
      retry: (failureCount, error) =>
        error?.name !== CLASSROOM_NOT_FOUND && failureCount < 3,
    },
  );

  const isClassroomNotFound =
    classroomQuery.error?.name === CLASSROOM_NOT_FOUND;

  useEffect(() => {
    if (classroomQuery.error && !isClassroomNotFound) {
      logger.error(`Error loading classroom ${classroomId}: ${classroomQuery.error}`);
    }
  }, [classroomQuery.error, isClassroomNotFound, classroomId]);

  const classroom = useMemo(
    () =>
      classroomQuery.isSuccess &&
        classroomQuery.data &&
        classroomQuery.data.classroomId
        ? classroomQuery.data
        : {},
    [classroomQuery.data, classroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([classroom]);
  }, [classroom, markCommunityAccessRestricted]);

  useEffect(() => {
    if (classroom.visibility === "archived" && !classroom.isCreator) {
      openSnackbar("this_classroom_is_archived", "error");
      navigate("/classrooms");
    }
  }, [classroom, navigate]);

  const {
    learnLang,
    forLang,
    classroomName,
    collaborators,
    createdBy,
    createdByName,
    vchatEnabled,
    vchatEnabledInSettings,
    isCreator,
  } = classroom;

  // Resolve friendly labels for collaborators stored as a Cognito userId
  // (e.g. "Wayside-SAML_<id>") so they render as a name rather than the raw id.
  // Emails are left as-is. Display-only — the stored values are unchanged.
  const [resolvedCollaboratorLabels, setResolvedCollaboratorLabels] = useState(
    {},
  );
  const collaboratorsKey = Array.isArray(collaborators)
    ? collaborators.join(",")
    : "";
  useEffect(() => {
    let active = true;
    if (collaboratorsKey) {
      usersHelpers
        .fetchCollaboratorLabels(collaboratorsKey.split(","))
        .then((labels) => {
          if (active) {
            setResolvedCollaboratorLabels(labels);
          }
        });
    } else {
      setResolvedCollaboratorLabels({});
    }
    return () => {
      active = false;
    };
  }, [collaboratorsKey]);

  // Seed a placeholder for userid collaborators that haven't resolved yet, so the
  // raw "Wayside-SAML_<id>" never flashes in the chip before the name loads.
  // Emails are omitted (chip falls back to showing the email itself).
  const collaboratorLabels = useMemo(() => {
    const map = {};
    (collaboratorsKey ? collaboratorsKey.split(",") : []).forEach((c) => {
      if (c && !c.includes("@")) {
        map[c] = resolvedCollaboratorLabels[c] || "…";
      }
    });
    return map;
  }, [collaboratorsKey, resolvedCollaboratorLabels]);

  const classroomAssignmentQuery = assignmentQuerys.useAssignments(
    async (classroomId, filters) => {
      const assignments = await getClassroomAssignments(classroomId, filters);
      return assignments.Items;
    },
    {
      classroomId,
      filters: {
        memberId: isCreator ? undefined : username,
        limit: config.limits.classroomAssignments,
      },
      memberId: username,
    },
    { enabled: !!classroomId && !!username && classroomQuery.isSuccess },
  );

  const assignments = useMemo(() => {
    return classroomAssignmentQuery.isSuccess && classroomAssignmentQuery.data
      ? classroomAssignmentQuery.data
      : [];
  }, [classroomAssignmentQuery.isSuccess, classroomAssignmentQuery.data]);

  const assignmentIds = useMemo(() => {
    if (
      assignmentId &&
      (!assignments ||
        !assignments.some((a) => a.assignmentId === assignmentId))
    ) {
      return [assignmentId];
    }

    return assignments.map((a) => a.assignmentId);
  }, [assignments, assignmentId]);

  const classroomCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      return formattedCourses;
    },
    {
      filters: {
        courseIds: selectedCourses.toString(),
        verbose: false,
        limit: config.limits.courses,
      },
    },
    {
      enabled:
        !!username &&
        Array.isArray(selectedCourses) &&
        selectedCourses.length > 0,
    },
  );

  async function refreshClassroomCourses() {
    try {
      await invalidateCache(DataType.CLASSROOM_COURSES_UPDATE, {
        classroomCourseIds: selectedCourses.toString(),
      });
      await classroomCoursesQuery.refetch();
      openSnackbar("Courses details updated", "success");
    } catch (error) {
      openSnackbar("problem", "error");
      logger.error(`Error updating course details: ${error}`);
    }
  }

  // User-triggered refresh: invalidate the backend cache, then refetch.
  async function refreshAssignments() {
    try {
      if (!isCreator) {
        await invalidateCache(DataType.ASSIGNMENT_MEMBER, {
          classroomId,
          memberId: username,
          limit: config.limits.classroomAssignments,
        });
      } else if (isCreator) {
        await invalidateCache(DataType.ASSIGNMENTS, {
          classroomId,
        });
      }
    } catch (error) {
      logger.error(`Error invalidating assignments cache: ${error}`);
    }
    await refreshAssignmentsPoll();
  }

  // Background poll: refetch only, no cache invalidation.
  async function refreshAssignmentsPoll() {
    try {
      await classroomAssignmentQuery.refetch();
    } catch (error) {
      logger.error(`Error refetching assignments: ${error}`);
    }
  }

  const assignmentMembersQuery = assignmentQuerys.useAssignmentMembers(
    async (filters) => {
      const members = await getAssignmentMembers(filters);
      return members;
    },
    {
      filters: {
        assignmentId,
        classroomId,
        assignmentIds,
        limit: config.limits.classroomMembers,
      },
    },
    { enabled: !!classroomId && assignmentIds.length > 0 },
  );
  const assignmentMembers = useMemo(() => {
    if (!assignmentMembersQuery.isSuccess || !assignmentMembersQuery.data)
      return {};
    return Object.fromEntries(
      Object.entries(assignmentMembersQuery.data).map(
        ([assignmentId, members]) => [
          assignmentId,
          Array.isArray(members)
            ? members.map((member) => member?.memberId)
            : [],
        ],
      ),
    );
  }, [assignmentMembersQuery.data, assignmentMembersQuery.isSuccess]);
  const creatorSubscriptionQuery = userQuerys.useSubscription(
    async (userId) => {
      const response = fetchCreatorSubscription(userId);
      return response;
    },
    {
      userId: createdBy,
    },
    {
      enabled: !!createdBy,
    },
  );

  const creatorSubscription = useMemo(
    () =>
      creatorSubscriptionQuery.isSuccess &&
        creatorSubscriptionQuery.data &&
        creatorSubscriptionQuery.data.plan
        ? creatorSubscriptionQuery.data
        : {},
    [creatorSubscriptionQuery.data, creatorSubscriptionQuery.isSuccess],
  );

  const { membersQuery, memberQuery, isMember, isChallengeModeStudent, isReadWriteModeStudent } =
    useClassroomState({
      classroomId,
      username,
      memberId: username,
      getClassroomMember,
      getClassroomMembers,
      visible: true,
    });

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const isWaysideUser = username?.startsWith("Wayside");

  const isVideoChatEnabled =
    (isWaysideUser || isUserInternal) &&
    (vchatEnabled === true ||
      localStorage.getItem("vchat-enabled") === "true" ||
      (isCreator && vchatEnabled !== false) ||
      (isMember && vchatEnabled !== false && vchatEnabledInSettings));
  const isVideoChatEnabledInSettings =
    (isVideoChatEnabled && vchatEnabledInSettings === true) ||
    localStorage.getItem("vchat-enabled") === "true";
  const isNualangLiveEnabled = creatorSubscription?.isPaidUser;
  // Discuss AI feedback: on only when the classroom's all-AI and Discuss
  // switches both allow it.
  const isDiscussAIFeedbackEnabled = isClassroomDiscussAIFeedbackEnabled(classroom);

  const {
    scheduledMeetings,
    selectedRecording,
    setSelectedRecording,
    handleBatchGradeConversations,
    handleCreateFeedback,
    aiGrade,
    goToTimestamp,
    conversation,
    s3EventsUrl,
    getSubmissionsQuery,
    submissionsData,
    meetingRecsData,
    getInvitesQuery,
    s3Url,
    resetVideoState,
    setS3Url,
    setTranscriptUrl,
    recordingFeedback,
    playerRef,
    formattedScheduleMeeting,
    handleJoinMeeting,
    createMeetingDialog,
    setCreateMeetingDialog,
    createdDiscussions,
    refreshDiscussions,
    recordingData,
    refreshRecordings,
    saveTranscript,
    handleArchiveDiscussions,
    handleArchiveRecording,
    handleUpdateDiscussions,
    setSelectedDiscussion,
    editBackgroundImages,
    editMeetingImages,
    imageLoading,
    startTimes,
  } = useVideoChat({
    attributes,
    username,
    classroomId,
    startLoading,
    stopLoading,
    openSnackbar,
    preferred_username,
    picture,
    isVideoChatEnabled,
    isVideoChatEnabledInSettings,
    t,
    classroomMembersFormatted,
    isCreator,
    learnLang,
    isDiscussAIFeedbackEnabled,
  });

  const {
    isSelectExerciseOpen,
    createdGame,
    handleCreateLiveGame,
    handleOpenSelectExercise,
    handleCloseSelectExercise,
  } = useClassroomLiveGame({
    classroomId,
    startLoading,
    stopLoading,
    openSnackbar,
    isNualangLiveEnabled,
    navigate,
    username,
    authData,
    isClassroomCreator: classroom?.isCreator,
    context: "view-classroom",
  });

  const groupCoursesIds = (subscription.groupSubscriptions || []).flatMap((g) => {
    if (g.courses && g.courses.length) {
      return g.courses;
    }
    return [];
  });

  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false);
  const [isFindCoursesOpen, setIsFindCoursesOpen] = useState(false);

  const classroomMembers =
    membersQuery.isSuccess &&
      Array.isArray(membersQuery.data.Items) &&
      membersQuery.data.Items.length
      ? membersQuery.data.Items
      : [];

  const classroomMembersStringified = JSON.stringify(classroomMembers);

  const memberCount = membersQuery.isSuccess
    ? membersQuery.data.Items.length
    : 0;

  // Save an initial value of selected courses, that we can restore on cancel
  useEffect(() => {
    if (classroom && classroom.courses && classroom.courses.length) {
      setInitialSelectedCourses([...classroom.courses]);
    } else {
      setInitialSelectedCourses([]);
    }
  }, [classroom]);

  useEffect(() => {
    async function formatClassroomMembers() {
      if (classroomMembers.length) {
        const results = await classroomHelpers.formatMembers(
          classroomMembers || [],
        );
        setClassroomMembersFormatted(results);
      } else {
        setClassroomMembersFormatted([]);
      }
    }
    formatClassroomMembers();
  }, [classroomMembersStringified]);

  // Mutations
  const updateClassroomMutation = classroomQuerys.useUpdateClassroom(
    updateClassroomAPI,
    {
      onSuccess: () => {
        openSnackbar("classroom_updated", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            createdBy: username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const addCoursesToClassroomMutation = classroomQuerys.useUpdateClassroom(
    updateClassroomAPI,
    {
      onSuccess: async () => {
        await classroomCoursesQuery.refetch();
        openSnackbar("courses_added", "success");
      },
      onError: (error) => {
        openSnackbar("problem_adding_courses", "error");
        logger.error(`Error adding courses to classroom: ${error}`);
      },
    },
  );

  const updateClassroomMemberMutation =
    classroomQuerys.useUpdateClassroomMember(updateClassroomMember, {
      onSuccess: () => {
        openSnackbar("member_updated", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            classroomId,
            username,
          ),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    });

  const handleUpdateClassroomMember = async (classroomId, memberId, values) => {
    updateClassroomMemberMutation.mutate({
      classroomId: classroomId,
      username: memberId,
      values,
    });
  };

  const deleteClassroomMutation = classroomQuerys.useDeleteClassroom(
    deleteClassroomAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            createdBy: username,
            verbose: false,
          }),
        });
        openSnackbar("classroom_deleted", "success");
        navigate("/classrooms#active");
      },
      onError: () => {
        openSnackbar("problem_deleting", "error");
      },
    },
  );
  const joinClassroomMutation = classroomQuerys.useJoinClassroom(
    createClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("joined_classroom", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: variables.username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const leaveClassroomMutation = classroomQuerys.useLeaveClassroom(
    deleteClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("left_classroom", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: variables.username,
            verbose: false,
          }),
        });
        navigate("/classrooms");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const deleteClassroomMemberMutation =
    classroomQuerys.useDeleteClassroomMember(deleteClassroomMember, {
      onMutate: async (removedMember) => {
        const queryKey = classroomQuerys.classroomKeys.itemMembers(
          classroomId,
          username,
        );

        await queryClient.cancelQueries({ queryKey });

        const previousMembers = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, (old) => ({
          ...old,
          Count: old?.Count - 1,
          Items:
            old?.Items?.filter((m) => m.memberId !== removedMember.username) ||
            [],
        }));
        return { previousMembers };
      },
      onSettled: (data, error, variables) => {
        // Error or success... doesn't matter!
        stopLoading();
        // Refetch on success or failure to ensure data is correct after optimistic update
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            username,
          ),
        });
      },
      onSuccess: async () => {
        openSnackbar(t("member_removed_from_classroom"), "success");
      },
      onError: (error, onMutateResult) => {
        openSnackbar("problem", "error");
        if (onMutateResult?.previousMembers) {
          queryClient.setQueryData(
            classroomQuerys.classroomKeys.itemMembers(
              variables.classroomId,
              username,
            ),
            onMutateResult.previousMembers,
          );
        }
      },
    });

  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/classrooms/${variables.classroomId}/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  useEffect(() => {
    if (classroom && classroom.courses && classroom.courses.length) {
      setSelectedCourses(classroom.courses);
    }
    return () => {
      setSelectedCourses([]);
    };
  }, [classroom, setSelectedCourses]);

  const handleDeleteClassroom = async () => {
    startLoading();
    deleteClassroomMutation.mutate({ classroomId, username });
  };

  const handleViewCourseTopic = (cId, sectionId, topicId, classroomId) => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${cId}/${sectionId}/${topicId}`);
    } else {
      navigate(`/courses/${cId}/${sectionId}/${topicId}`);
    }
  };

  const handleOpenCreateCourse = () => {
    window.scrollTo(0, 0);
    setIsCreateCourseOpen(true);
  };
  const handleCloseCreateCourse = () => setIsCreateCourseOpen(false);

  const handleOpenFindCourses = () => {
    window.scrollTo(0, 0);
    setIsFindCoursesOpen(true);
  };
  const handleCloseFindCourses = () => setIsFindCoursesOpen(false);

  const handleAddFindCourses = async (newlySelectedCourses = []) => {
    if (newlySelectedCourses.length === 0) {
      handleCloseFindCourses();
      return;
    }
    startLoading();
    const newlySelectedCourseIds = newlySelectedCourses.map((course) => course.courseId);
    const updatedCourses = [
      ...new Set([...(classroom.courses || []), ...newlySelectedCourseIds]),
    ];
    try {
      await addCoursesToClassroomMutation.mutateAsync({
        classroomId,
        values: { courses: updatedCourses },
        username,
      });
    } catch (error) {
      openSnackbar("problem_adding_courses", "error");
      logger.error(`Error adding courses to classroom: ${error}`);
    } finally {
      stopLoading();
      handleCloseFindCourses();
    }
  };

  const handleJoinClassroom = () => {
    setNewClassroom(classroom);
    if (memberCount >= memberLimit) {
      openSnackbar("classroom_member_limit_reached", "error");
    } else if (classroom.visibility === "private") {
      setIsJoinClassroomDialogOpen(true);
    } else {
      startLoading();
      joinClassroomMutation.mutate({ classroomId, username });
    }
  };

  const handleLeaveClassroom = async () => {
    startLoading();
    leaveClassroomMutation.mutate({ classroomId, username });
  };

  const handleDeleteClassroomMember = async (memberId) => {
    startLoading();
    deleteClassroomMemberMutation.mutate({ classroomId, username: memberId });
  };

  const handleStartCourse = async (course) => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (
        course.visibility === "private" &&
        classroom.visibility === "public"
      ) {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({
          courseId: course.courseId,
          username,
          classroomId,
        });
      }
    }
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleUpdateClassroom = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      if (
        values.visibility &&
        values.visibility !== "public" &&
        values.visibility !== "private" &&
        values.visibility !== "archived"
      ) {
        throw new Error();
      }
      if (values.collaborators) {
        // values.collaborators = values.collaborators.trim().split(',').map((c) => c.trim()).filter((c) => c !== '');
        sendCollaboratorEmail(
          values.collaborators,
          classroomName,
          "classroom",
          collaborators,
          classroomId,
          createdByName,
          attributes["custom:learnLang"],
        );
      }
      if (values.picture && values.picture !== classroom.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadClassroomImage(
            base64String[1] || base64String[0],
            classroomId,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.banner && values.banner !== classroom.banner) {
        values.bannerWarnDetails = {};
        const base64String = values.banner.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadClassroomImage(
            base64String[1] || base64String[0],
            classroomId,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.bannerWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.banner = picture;
          values.bannerS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      updateClassroomMutation.mutate({ classroomId, values, username });
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    } catch (error) {
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (newCollaborator) {
      invalidateCache(DataType.CLASSROOM, {
        classroomId,
      });
    }
  }, [classroomId, newCollaborator]);

  const handleViewCourse = (courseId, classroomId) => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}`);
    } else {
      navigate(`/courses/${courseId}`);
    }
  };

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const handleSearchClassrooms = isCommunityAccessRestricted
    ? undefined
    : () => navigate("/classrooms/search");

  const getMemberDetails = (membersList) => usersHelpers.fetchMembersDetails(membersList);

  const handleRemoveCourse = (courseId) => {
    const filteredCourses = selectedCourses.filter((id) => id !== courseId);
    handleUpdateClassroom(
      {
        courses: filteredCourses,
      },
      {},
    );
  };

  const handleCreateCourse = () => {
    navigate(
      `/courses/create?classroomId=${classroomId}&learnLang=${learnLang}&forLang=${forLang}`,
    );
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const userInitialValues = {
    picture: attributes.picture || "",
  };

  const updateUserMutation = userQuerys.useUpdateUser(
    usersHelpers.updateUserAttributes,
    {
      onSuccess: (data) => {
        setUser(data);
        queryClient.invalidateQueries({
          queryKey: userQuerys.userKeys.item(username),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleUpdateUser = (values) => {
    updateUserMutation.mutate({ userId: username, values });
  };

  const saveAvatarToDynamo = (avatarAttributes) => {
    const value = {
      avatar: avatarAttributes,
    };
    handleUpdateUser(value);
  };

  // Sort in the same order as the selected courses array so invalidating the cache works e.g ?courseIds=a,b,c
  const classroomCourses =
    classroomCoursesQuery.isSuccess &&
      Array.isArray(classroomCoursesQuery.data) &&
      classroomCoursesQuery.data.length
      ? [...classroomCoursesQuery.data].sort(
        (a, b) =>
          selectedCourses.indexOf(a.courseId) -
          selectedCourses.indexOf(b.courseId),
      )
      : [];

  // const handleFilterChange = (event) => {
  //   const { target } = event;
  //   const { value } = target;
  //   setFilter(value);
  // };

  // const handleReportTypeChange = (event) => {
  //   const { target } = event;
  //   const { value } = target;
  //   setReportType(value);
  // };

  const handleCreateAssignment = () => {
    navigate(`/classrooms/${classroomId}/assignments/create`);
  };

  const handleEditAssignment = (assignment) => {
    const { assignmentId } = assignment;
    navigate(`/classrooms/${classroomId}/${assignmentId}/assignments/edit`, {
      state: { assignment },
    });
  };

  const deleteAssignmentMutation = assignmentQuerys.useDeleteAssignment(
    deleteAssignmentAPI,
    {
      onSettled: () => {
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar(t("assignment_deleted"), "success");
      },
      onError: () => {
        openSnackbar("problem_deleting", "error");
      },
    },
  );

  const handleDeleteAssignment = async (classroomId, assignmentId) => {
    startLoading();

    try {
      await deleteAssignmentMutation.mutateAsync({
        classroomId,
        assignmentId,
      });
    } catch (error) {
      // Handle error if the mutation fails
      console.error("Error deleting assignment:", error);
    } finally {
      stopLoading();
    }
  };

  const duplicateCourseMutation = courseQuerys.useDuplicateCourse(copyCourse, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: username,
          verbose: false,
          // Get all using high limit
          limit: config.limits.courses,
        }),
      });
      if (data && data.Item && data.Item.courseId) {
        const { courseId } = data.Item;
        if (courseId && classroomId) {
          await classroomHelpers.addCourseToClassroom(courseId, classroomId);
          queryClient.invalidateQueries({
            queryKey: classroomQuerys.classroomKeys.item(classroomId, username),
          });
        }
        if (courseId && classroomId) {
          stopLoading();
          openSnackbar("created", "success");
          navigate(`/classrooms/${classroomId}/${courseId}`);
        } else if (courseId) {
          stopLoading();
          openSnackbar("created", "success");
          navigate(`/courses/${courseId}`);
        }
      }
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const handleDuplicateCourse = async (values) => {
    startLoading();
    await duplicateCourseMutation.mutate({
      values: {
        ...values,
        ...(classroomId && { classroomId }),
        ...props.authData,
      },
    });
  };

  const isAssignedCoursesDifferent = !(
    JSON.stringify([...(classroom.assignedCourses || [])].sort()) ===
    JSON.stringify([...(classroom.courses || [])].sort())
  );

  const assignedCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      return formattedCourses;
    },
    {
      filters: {
        courseIds: classroom?.assignedCourses,
        verbose: false,
        limit: config.limits.courses,
      },
    },
    {
      enabled: !!(
        classroom.assignedCourses &&
        classroom.assignedCourses.length > 0 &&
        isAssignedCoursesDifferent
      ),
    },
  );

  const assignedCoursesQueryData =
    assignedCoursesQuery.isSuccess &&
      Array.isArray(assignedCoursesQuery.data) &&
      assignedCoursesQuery.data.length
      ? assignedCoursesQuery.data
      : [];

  const assignedCourses =
    classroom?.assignedCourses?.length > 0 && isAssignedCoursesDifferent
      ? assignedCoursesQueryData
      : classroomCourses;

  // Add a combined "initial loading" flag for critical queries so we don't show
  // a half-hydrated page when those sub-queries are still in their first load.
  // Comment out queries that are not critical
  // The creatorSubscription query is critical to avoid the "not subscribed" dialog appearing
  const isAnyInitialLoading =
    !!classroomQuery?.isLoading || !!creatorSubscriptionQuery?.isLoading;
  // || !!classroomCoursesQuery?.isLoading
  // || !!membersQuery?.isLoading
  // || !!assignedCoursesQuery?.isLoading
  // || !!classroomAssignmentQuery?.isLoading


  const createCourseMutation = courseQuerys.useCreateCourse(createCourseAPI, {
    onMutate: () => {
      startLoading();
    },
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      const newCourse = data.Item;
      openSnackbar("course_created", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: authData.username,
          verbose: false,
        }),
      });

      await createCourseSection(newCourse.courseId, { sectionName: t("section") });
      handleUpdateClassroom({ courses: [...selectedCourses, newCourse.courseId] }, {});
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    },
  });

  const createCourse = (values) => {
    const {
      courseName,
      description,
      learnLang,
      forLang,
      visibility,
      enrolmentKey,
    } = values;

    if (!courseName || !description || !learnLang || !forLang || !visibility) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (visibility === "private" && !enrolmentKey) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (values.enrolmentKey === "") {
      values.enrolmentKey = null;
    }

    return createCourseMutation.mutateAsync({ values });
  };

  const handleQuickCreateCourse = async (values) => {
    startLoading();
    try {
      const { visibility } = values;
      if (visibility !== "public" && visibility !== "private") {
        throw new Error();
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadCourseImage(base64String[1] || base64String[0], uuidv4());
        if (moderated || warn) {
          if (verificationStatus && verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.pictureWarnDetails = {
              user: authData.username,
              labels: moderatedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.banner) {
        const base64String = values.banner.split(",");
        const [bannerUrl, bannerS3Key] =
          await UploadCourseImage(base64String[1] || base64String[0], uuidv4());
        if (bannerUrl) {
          values.banner = bannerUrl;
          values.bannerS3Key = bannerS3Key;
        } else {
          delete values.banner;
        }
      }
      await createCourse(values);
    } catch (error) {
      openSnackbar("Error creating course", "error");
      logger.error(error);
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      {isAnyInitialLoading ? (
        <ViewClassroomScreen
          t={t}
          isLoading={isAnyInitialLoading}
          placeholderImageUrl={ClassroomImage}
          classroom={{}}
        ></ViewClassroomScreen>
      ) : (
        <ViewClassroomScreen
          t={t}
          email={email}
          isLoading={classroomQuery.isLoading}
          placeholderImageUrl={ClassroomImage}
          coursePlaceholderImageUrl={NoCoursesImage}
          memberPlaceholderImageUrl={MembersImage}
          classroom={classroom}
          classroomLoadError={isClassroomNotFound ? null : classroomQuery.error}
          isRetryingClassroom={classroomQuery.isFetching}
          handleRetryClassroom={() => classroomQuery.refetch()}
          courses={classroomCourses}
          assignedCourses={assignedCourses}
          isMember={isMember}
          featureFlags={featureFlags}
          members={
            classroomMembersFormatted.length
              ? classroomMembersFormatted
              : classroomMembers
          }
          isCoursesLoading={classroomCoursesQuery.isLoading}
          handleLeaveClassroom={handleLeaveClassroom}
          handleDeleteClassroom={handleDeleteClassroom}
          handleOpenCreateCourse={handleOpenCreateCourse}
          handleOpenFindCourses={handleOpenFindCourses}
          refreshClassroomCourses={refreshClassroomCourses}
          handleJoinClassroom={handleJoinClassroom}
          handleDeleteClassroomMember={handleDeleteClassroomMember}
          handleUpdateClassroomMember={handleUpdateClassroomMember}
          handleStartCourse={handleStartCourse}
          handleLeaveCourse={handleLeaveCourse}
          handleViewCourse={handleViewCourse}
          handleSearchClassrooms={handleSearchClassrooms}
          getMemberDetails={getMemberDetails}
          handleViewCourseTopic={handleViewCourseTopic}
          handleQuickCreateCourse={handleQuickCreateCourse}
          membersProps={{
            handleViewActivity,
            showSortControl: true,
          }}
          languages={languages}
          forLanguages={forLanguages}
          collaboratorLabels={collaboratorLabels}
          handleUpdateClassroom={handleUpdateClassroom}
          handleRemoveCourse={
            classroom && classroom.isCreator ? handleRemoveCourse : null
          }
          fileSizeLimit={config.fileSizeLimit}
          openSnackbar={openSnackbar}
          getCourseSections={getCourseSections}
          getRoleplays={getCourseSectionTopicRoleplays}
          getCourseSectionTopicPhraseLists={getCourseSectionTopicPhraseLists}
          getCourses={getCourses}
          memberId={username}
          getCourseMember={getCourseMember}
          getCourseMembers={getCourseMembers}
          username={username}
          preferred_username={preferred_username}
          subscription={subscription}
          isTeacher={attributes["custom:role"] === "teach"}
          creatorSubscription={creatorSubscription}
          handleViewUserProfile={handleViewUserProfile}
          fetchMemberCourseCompletions={getMemberCourseCompletions}
          mainSiteUrl={config.SITE_URL}
          attributes={attributes}
          trackRecommendedEvent={trackRecommendedEvent}
          authenticated={authenticated}
          handleSignIn={handleSignIn}
          handleCreateCourse={handleCreateCourse}
          userInitialValues={userInitialValues}
          saveAvatarToDynamo={saveAvatarToDynamo}
          freeTrial={freeTrial}
          handleDuplicateCourse={handleDuplicateCourse}
          handleUpdateUserLearnLang={(classroomLearnLang) => {
            const values = {
              "custom:learnLang": classroomLearnLang || "",
            };
            handleUpdateUser(values);
          }}
          handleUpdateAvatar={async (e) => {
            const { value } = e.target;
            const values = userInitialValues;
            if (value && value !== userInitialValues.picture) {
              values.pictureWarnDetails = {};
              const base64String = value.split(",");
              const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
                await UploadUserImage(
                  base64String[1] || base64String[0],
                  username,
                );
              if (moderated || warn) {
                if (verificationStatus === "approved" || warn) {
                  const confirmation = await confirm(
                    `${t("warning")}`,
                    `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                    `${t("use_anyways")}`,
                  );
                  values.pictureWarnDetails = {
                    user: username,
                    labels: moderatedLabels.join(", "),
                  };
                  if (confirmation !== true) {
                    return;
                  }
                } else {
                  const moderationReason = moderatedLabels[0];
                  handleOpenModerateDialog(moderationReason);
                  return;
                }
              }
              if (picture) {
                values.picture = picture;
                values.pictureS3Key = pictureS3Key;
              } else {
                delete values.picture;
              }
            }
            handleUpdateUser(values);
          }}
          vchatProps={{
            handleDeleteDiscussion: handleArchiveDiscussions,
            handleArchiveSubmissions: handleArchiveRecording,
            handleEditDiscussion: handleUpdateDiscussions,
            setSelectedDiscussion,
            editBackgroundImages,
            editMeetingImages,
            saveTranscript,
            discussions: scheduledMeetings,
            recordingDiscussions: recordingData,
            selectedRecording: selectedRecording,
            setSelectedRecording: setSelectedRecording,
            getMeeting: getMeeting,
            handleBatchGradeConversations: handleBatchGradeConversations,
            handleCreateFeedback: handleCreateFeedback,
            aiGrade: aiGrade,
            isDiscussAIFeedbackEnabled: isDiscussAIFeedbackEnabled,
            goToTimestamp: goToTimestamp,
            conversation: conversation,
            s3EventsUrl: s3EventsUrl,
            loading,
            imageLoading,
            submissionsTableData: submissionsData,
            submissions: submissionsData,
            getRecordings: getSubmissionsQuery.refetch,
            getSchedules: getInvitesQuery.refetch,
            refreshRecordings,
            s3Url: s3Url,
            resetVideoState: resetVideoState,
            setS3Url: setS3Url,
            setTranscriptUrl: setTranscriptUrl,
            recordingFeedback: recordingFeedback,
            playerRef: playerRef,
            formattedScheduleMeeting: formattedScheduleMeeting,
            handleJoinMeeting: handleJoinMeeting,
            createMeetingDialog: createMeetingDialog,
            setCreateMeetingDialog: setCreateMeetingDialog,
            createdDiscussions: createdDiscussions,
            refreshDiscussions: refreshDiscussions,
            classroomMembers: [...classroomMembersFormatted],
            meetingRecsData,
            isVideoChatEnabled,
            isVideoChatEnabledInSettings,
            startTimes,
          }}
          handleCreateLiveGame={handleCreateLiveGame}
          handleJoinLiveGame={(gameId) => {
            navigate(`/classrooms/${classroomId}/games/${gameId}`);
          }}
          isSelectExerciseOpen={isSelectExerciseOpen}
          handleCloseSelectExercise={handleCloseSelectExercise}
          handleOpenSelectExercise={handleOpenSelectExercise}
          createdGame={createdGame}
          isNualangLiveEnabled={isNualangLiveEnabled}
          isChallengeModeStudent={isChallengeModeStudent}
          isReadWriteModeStudent={isReadWriteModeStudent}
          handleCreateAssignment={handleCreateAssignment}
          handleEditAssignment={handleEditAssignment}
          assignments={assignments}
          refreshAssignments={refreshAssignments}
          refreshAssignmentsPoll={refreshAssignmentsPoll}
          deleteAssignment={handleDeleteAssignment}
          isLoadingAssignments={classroomAssignmentQuery.isLoading}
          progressHelpers={progressHelpers}
          completionHelpers={completionHelpers}
          assignmentMembersById={assignmentMembers}
          makeChatGptApiRequest={makeChatGptApiRequest}
          siteLanguage={i18n.language}
        ></ViewClassroomScreen>
      )}
      <QuickCreateCourseDialog
        t={t}
        open={isCreateCourseOpen}
        handleClose={handleCloseCreateCourse}
        handleCreate={handleQuickCreateCourse}
        learnLang={learnLang}
        forLang={forLang}
        languages={config.languages}
        forLanguages={config.forLanguages}
        fileSizeLimit={config.fileSizeLimit}
        verificationStatus={verificationStatus}
        openSnackbar={openSnackbar}
      />
      <FindCoursesDialog
        t={t}
        open={isFindCoursesOpen}
        handleClose={handleCloseFindCourses}
        onAddCourses={handleAddFindCourses}
        username={username}
        authData={authData}
        formatCourses={courseHelpers.formatCourses}
        groupCourseIds={groupCoursesIds}
        groupCoursesLimit={config.limits.courses}
        alreadyAddedCourseIds={classroom.courses || []}
        isCommunityAccessRestricted={isCommunityAccessRestricted}
        learnLangOptions={Object.keys(languages)}
        forLangOptions={Object.keys(forLanguages)}
        defaultLearnLang={learnLang || "all"}
        defaultForLang={forLang || "all"}
      />
    </>
  );
}

ViewClassroom.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default ViewClassroom;
